// Service for fetching retailer analytics from S3

import FetchClient, { type ApiResponse } from "./FetchClient";
import type { RetailerType } from "../config/retailers";

// Retailers that have analytics (exclude woot which doesn't have analytics)
export type AnalyticsRetailerType = Extract<
  RetailerType,
  "walmart" | "walmart_online" | "amazon" | "home_depot" | "dollar_general" |
  "target" | "target_online" | "bestbuy" | "costco" | "lowes" | "kohls"
>;

export interface StoreAnalytics {
  storeId: string;
  totalProductsFirstInserted: number;
  productsUploaded: number;
  averageDiscount: number;
  usersSearched: number;
  categoriesExpected: number;
  productsFoundPerCategory: Record<string, number>;
}

export interface AnalyticsMetadata {
  totalProductsFirstInserted: number;
  totalProductsUploaded: number;
  totalUsers: number;
  usersWithProducts: number;
  usersWith0Products: number;
  storesWith0Products: string[];
  productBreakdown: number[]; // [0-10, 11-50, 51-100, 100+]
}

export interface RetailerAnalyticsData {
  storeAnalytics: StoreAnalytics[];
  metadata: AnalyticsMetadata;
}

/**
 * Get the latest analytics file for a retailer from S3
 * @param retailer - Retailer type (walmart, home_depot, or dollar_general)
 * @returns Promise with analytics data
 */
export async function getRetailerAnalytics(
  retailer: AnalyticsRetailerType
): Promise<RetailerAnalyticsData> {
  // Try to get today's file first, then try yesterday's if today doesn't exist
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateFormats = [
    today.toISOString().split("T")[0], // YYYY-MM-DD
    yesterday.toISOString().split("T")[0],
  ];

  // Try each date until we find a file
  for (const dateStr of dateFormats) {
    try {
      // Try fetching from S3 via backend endpoint
      const response = await FetchClient.get<ApiResponse<RetailerAnalyticsData>>(
        `/admin/analytics-file?retailer=${retailer}&date=${dateStr}`
      );

      if (response && response.data) {
        return response.data;
      }
    } catch (error) {
      console.warn(`Failed to fetch analytics for ${dateStr}:`, error);
      continue;
    }
  }

  // If all dates fail, try direct S3 URL (if bucket is public)
  const latestDate = dateFormats[0];
  const fileNameMap: Record<AnalyticsRetailerType, string> = {
    walmart: `walmart-analytics-${latestDate}.json`,
    walmart_online: `walmart-online-analytics-${latestDate}.json`,
    amazon: `amazon-analytics-${latestDate}.json`,
    home_depot: `home-depot-analytics-${latestDate}.json`,
    dollar_general: `dollar-general-analytics-${latestDate}.json`,
    target: `target-analytics-${latestDate}.json`,
    target_online: `target-online-analytics-${latestDate}.json`,
    bestbuy: `bestbuy-analytics-${latestDate}.json`,
    costco: `costco-analytics-${latestDate}.json`,
    lowes: `lowes-analytics-${latestDate}.json`,
    kohls: `kohls-analytics-${latestDate}.json`,
  };
  const fileName = fileNameMap[retailer];

  try {
    const s3Url = `https://dealhawk-analytics.s3.us-east-2.amazonaws.com/${fileName}`;
    const response = await fetch(s3Url);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error("Failed to fetch from S3:", error);
  }

  throw new Error(
    `Failed to fetch analytics for ${retailer}. No data available for recent dates.`
  );
}
